import {
  Attribute,
  Directive,
  Injector,
  Optional
} from '@angular/core';

import { addClass, removeClass, switchClass, toggleClass, runtimeEventManager } from "@wm/core";

import { IWidgetConfig, StylableComponent, provideAsWidgetRef, propFactory as PropertiesFactory, Parent, register, styler, getNavClass } from "@wm/components-base";
import {PageDirective} from "../page.directive";

const DEFAULT_CLS = 'app-left-panel left-panel-collapsed';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-left-panel',
  hostClass: DEFAULT_CLS
};

export enum AnimationType {
  SLIDE_IN = 'slide-in',
  SLIDE_OVER = 'slide-over'
}

@Directive({
  selector: '[wmLeftPanel]',
  providers: [
    provideAsWidgetRef(LeftPanelComponent)
  ],
  exportAs: 'wmLeftPanel',
  standalone: true
})
export class LeftPanelComponent extends StylableComponent {
  static initializeProps = register('wm-left-panel');
  public $ele;
  public $page: any;
  public animation: AnimationType;
  public columnwidth: number;
  public expanded: boolean;
  public gestures: string;
  public xscolumnwidth: number;
  private _destroyCollapseActionListener: () => void;
  private isTabletApplicationType: boolean;
  markupManager: any;

  constructor(inj: Injector,
              @Optional() viewParent: Parent, @Optional() private page: PageDirective) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this.$ele = $(this.nativeElement);
    // TODO: layoutDirective needs to be added
    // this.$page = this.getAttr('spa') && layout && layout.$element || page && page.$element;
    this.$page = page && $(page.getNativeElement());

    this.$ele = this.$element;
    addClass(this.$page[0], 'left-panel-collapsed-container');
    // navHeight && addClass(this.$page[0], 'nav-'+ navHeight);
    if (this.columnwidth) {
      this.setLeftPanelWidth(['md', 'sm'], this.columnwidth);
    }
    this.isTabletApplicationType = $('.wm-app').hasClass('wm-tablet-app');
    if (this.isTabletApplicationType) {
      addClass(this.nativeElement, 'wm-tablet-app-left-panel');
    }

    runtimeEventManager.on('selected-canvas-change').subscribe(() => {
      this.widget.widgetProps = PropertiesFactory.getWidgetProperties(this.widgetType);
      if (this.expanded) {
        this.toggle();
      }
    });
    this.markupManager = runtimeEventManager.getService('markupManager');
  }

  public override onPropertyChange(key: string, nv: any, ov: any) {
    switch (key) {
      case 'animation' :
        if (nv === AnimationType.SLIDE_IN) {
          removeClass(this.$page[0], 'slide-over-left-panel-container');
          addClass(this.$page[0], 'slide-in-left-panel-container');
          this.setPageWidthAndPosition(['md', 'sm'], this.columnwidth);
          this.setPageWidthAndPosition(['xs'], this.xscolumnwidth);
        } else if (nv === AnimationType.SLIDE_OVER) {
          removeClass(this.$page[0], 'slide-in-left-panel-container');
          addClass(this.$page[0], 'slide-over-left-panel-container');
        }
        switchClass(this.nativeElement, nv, ov);
        break;
      case 'columnwidth':
        this.setLeftPanelWidth(['md', 'sm'], nv, ov);
        if (this.animation === AnimationType.SLIDE_IN) {
          this.setPageWidthAndPosition(['md', 'sm'], nv, ov);
        }
        break;
      case 'expanded':
        toggleClass(this.nativeElement, 'left-panel-expanded', nv);
        toggleClass(this.nativeElement, 'left-panel-collapsed', !nv);
        break;
      case 'xscolumnwidth':
        this.setLeftPanelWidth(['xs'], nv, ov);
        if (this.animation === AnimationType.SLIDE_IN) {
          this.setPageWidthAndPosition(['xs'], nv, ov);
        }
        break;
      case 'navtype':
        addClass(this.nativeElement, getNavClass(nv));
        removeClass(this.nativeElement, getNavClass(ov));
        break;
      case 'navheight':
        addClass(this.nativeElement, getNavClass(nv));
        removeClass(this.nativeElement, getNavClass(ov));
        if(nv === 'full') {
          this.markupManager.processMarkupForFullHeightNav();
        } else {
          this.markupManager.processMarkupForClippedNav();
        }
        runtimeEventManager.emit('nav-height-change', nv);
        break;
      default:
        super.onPropertyChange(key, nv, ov);
    }
  }

  private setLeftPanelWidth(devices: string[], newVal: number, oldVal?: number) {
    devices.forEach(device => {
      if (newVal) {
        addClass(this.nativeElement, `col-${device}-${newVal}`);
      }
      if (oldVal) {
        removeClass(this.nativeElement, `col-${device}-${oldVal}`);
      }
    });
  }

  private setPageWidthAndPosition(devices: string[], newVal: number | null, oldVal?: number) {
    devices.forEach(device => {
      if (newVal) {
        addClass(this.$page[0], `left-panel-container-${device}-${12 - newVal}`);
      }
      if (oldVal) {
        removeClass(this.$page[0], `left-panel-container-${device}-${12 - oldVal}`);
      }
    });
  }

  public collapse(): void {
    // addClass(this.nativeElement, 'swipee-transition');
    switchClass(this.nativeElement, 'left-panel-collapsed', 'left-panel-expanded');
    if (this.expanded) {
      setTimeout(() => {
        const leftNavButton = $('.app-header-action');
        leftNavButton.attr('aria-expanded', 'false');
        leftNavButton.focus();
      }, 50);
    }
    this.expanded = false;
    switchClass(this.$page[0], 'left-panel-collapsed-container', 'left-panel-expanded-container');
    if (this.animation === AnimationType.SLIDE_IN) {
      this.setPageWidthAndPosition(['md', 'sm'], null, this.columnwidth);
      this.setPageWidthAndPosition(['xs'], null, this.xscolumnwidth);
    }
    if (this._destroyCollapseActionListener) {
      this._destroyCollapseActionListener();
    }
    this.page.notify('wmLeftPanel:collapse');
  }

  public expand(): void {
    removeClass(this.nativeElement, 'hidden');
    removeClass(this.nativeElement, 'swipee-transition');
    switchClass(this.nativeElement, 'left-panel-expanded', 'left-panel-collapsed');
    this.expanded = true;
    if (!(this.isTabletApplicationType && this.animation === AnimationType.SLIDE_IN)) {
      this._destroyCollapseActionListener = this.listenForCollapseAction();
    }
    switchClass(this.$page[0], 'left-panel-expanded-container', 'left-panel-collapsed-container');
    if (this.animation === AnimationType.SLIDE_IN) {
      this.setPageWidthAndPosition(['md', 'sm'], this.columnwidth);
      this.setPageWidthAndPosition(['xs'], this.xscolumnwidth);
    }
    this.page.notify('wmLeftPanel:expand');
    $('.app-header-action').attr('aria-expanded', `${this.expanded}`);
  }

  public  toggle(): void {
    // if (this.isTabletApplicationType) {
    //   setTimeout(() => {
    //     this.expanded ? this.collapse() : this.expand();
    //   }, 50);
    // } else {
    //   this.$ele.swipeAnimation(this.expanded ? 'gotoLower' : 'gotoUpper');
    // }
    setTimeout(() => {
      this.expanded ? this.collapse() : this.expand();
    }, 50);
  }

  private listenForCollapseAction(): () => void {
    const eventName = 'click.leftNavToggle';
    let skipEvent = false;
    this.$ele.on(eventName, () => {
      skipEvent = true;
    });
    this.$page.on(eventName, ($event: Event) =>  {
      console.log($event);
      if (!skipEvent) {
        this.toggle();
      }
      skipEvent = false;
    });
    return () => {
      this.$ele.off(eventName);
      this.$page.off(eventName);
    };
  }
}

